'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { useSearchParams, useRouter } from 'next/navigation'
import { Mail, ArrowRight, Eye, EyeOff } from 'lucide-react'
import { createSupabaseClient } from '@/lib/supabase/client'
import { Logo } from '@/components/shared/logo'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useDict } from '@/lib/i18n/context'
import { captureAttribution } from '@/lib/attribution'
import { safeNextPath } from '@/lib/auth/safe-next'

type AuthMode = 'password' | 'magic-link' | 'forgot-password'

export default function LoginPage() {
  const dict = useDict()
  const router = useRouter()
  const searchParams = useSearchParams()
  // App-relative paths only — the raw value used to reach window.location.
  const next = safeNextPath(searchParams.get('next'))

  // Backward-compat: redirect old ?mode=signup links to the dedicated /signup route
  useEffect(() => {
    if (searchParams.get('mode') === 'signup') {
      const q = next ? `?next=${encodeURIComponent(next)}` : ''
      router.replace(`/signup${q}`)
    }
  }, [searchParams, router, next])

  // First-touch UTM / referrer capture. Runs once on mount.
  useEffect(() => { captureAttribution() }, [])

  // ?email= — a team invitation sends the invited address (app/invite).
  const [email, setEmail] = useState(searchParams.get('email') ?? '')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [authMode, setAuthMode] = useState<AuthMode>('password')
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')
  const supabase = createSupabaseClient()

  const callbackUrl = next
    ? `${typeof window !== 'undefined' ? window.location.origin : ''}/api/auth/callback?next=${encodeURIComponent(next)}`
    : `${typeof window !== 'undefined' ? window.location.origin : ''}/api/auth/callback`

  const signupHref = next ? `/signup?next=${encodeURIComponent(next)}` : '/signup'

  const handlePasswordSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) {
      setError(error.message)
    } else {
      window.location.href = next ?? '/'
    }
    setLoading(false)
  }

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: callbackUrl },
    })
    if (error) setError(error.message)
    else setSent(true)
    setLoading(false)
  }

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    const origin = typeof window !== 'undefined' ? window.location.origin : ''
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${origin}/reset-password`,
    })
    if (error) setError(error.message)
    else setSent(true)
    setLoading(false)
  }

  const handleGoogleSignIn = async () => {
    setLoading(true)
    setError('')
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: callbackUrl },
    })
    if (error) {
      setError(error.message)
      setLoading(false)
    }
  }

  const switchMode = (mode: AuthMode) => {
    setAuthMode(mode)
    setError('')
    setSent(false)
  }

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden bg-linear-to-br from-indigo-50 via-white to-violet-50">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-indigo-100/50" />
        <div className="absolute top-1/3 -left-16 h-48 w-48 rounded-full bg-violet-100/40" />
      </div>

      <div className="relative z-10 flex-1 flex flex-col px-6 sm:px-8">
        {/* Top bar — logo + signup link */}
        <header className="w-full max-w-md mx-auto w-full py-5 flex items-center justify-between">
          <Logo size={36} textClassName="text-lg font-bold" />
          <div className="text-sm text-muted-foreground">
            {dict.login.noAccount}{' '}
            <Link href={signupHref} className="text-primary hover:text-primary/80 font-medium">
              {dict.login.switchToSignUp}
            </Link>
          </div>
        </header>

        {/* Centered sign-in card — minimal, focused */}
        <div className="w-full max-w-md mx-auto flex-1 flex items-start pt-10 lg:pt-24">
          <div className="w-full">
            {sent ? (
              <div className="bg-card rounded-2xl ring-1 ring-border/50 shadow-lg p-8 text-center">
                <div className="flex justify-center mb-4">
                  <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <Mail className="h-6 w-6 text-primary" />
                  </div>
                </div>
                {authMode === 'forgot-password' ? (
                  <>
                    <h2 className="text-lg font-bold text-foreground mb-2">{dict.login.resetEmailSent}</h2>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {dict.login.resetEmailSentDesc}
                    </p>
                  </>
                ) : (
                  <>
                    <h2 className="text-lg font-bold text-foreground mb-2">{dict.login.checkEmail}</h2>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {dict.login.emailSentTo}<br />
                      <strong className="text-foreground">{email}</strong>
                    </p>
                    <p className="text-xs text-muted-foreground/70 mt-3">
                      {dict.login.clickLink}
                    </p>
                  </>
                )}
                <button
                  onClick={() => switchMode('password')}
                  className="mt-5 text-sm text-primary hover:text-primary/80 font-medium inline-flex items-center gap-1 transition-colors"
                >
                  {dict.login.backToSignIn}
                </button>
              </div>
            ) : (
              <>
                <div className="bg-card rounded-2xl ring-1 ring-border/50 shadow-lg p-8 sm:p-9">
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold text-foreground">
                      {authMode === 'forgot-password' ? dict.login.forgotPasswordTitle : dict.login.welcomeBack}
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      {authMode === 'forgot-password' ? dict.login.forgotPasswordSubtitle : dict.login.signInSubtitle}
                    </p>
                  </div>

                  {/* Google OAuth — hide on forgot-password */}
                  {authMode !== 'forgot-password' && (
                    <>
                      <button
                        onClick={handleGoogleSignIn}
                        disabled={loading}
                        className="w-full flex items-center justify-center gap-3 h-12 px-4 border border-border rounded-xl text-sm font-medium text-foreground bg-background hover:bg-accent disabled:opacity-50 transition-all duration-150 shadow-sm hover:shadow"
                      >
                        <svg className="w-4.5 h-4.5" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        {dict.login.googleSignIn}
                      </button>

                      <div className="relative my-5">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-border" />
                        </div>
                        <div className="relative flex justify-center text-xs text-muted-foreground">
                          <span className="bg-card px-3">{dict.login.orEmail}</span>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Password form (default sign-in path) */}
                  {authMode === 'password' && (
                    <form onSubmit={handlePasswordSignIn} className="space-y-3.5">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">
                          {dict.login.emailLabel}
                        </label>
                        <Input
                          type="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="your@email.com"
                          className="h-12"
                          autoComplete="email"
                        />
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <label className="block text-sm font-medium text-foreground">
                            {dict.login.passwordLabel}
                          </label>
                          <button
                            type="button"
                            onClick={() => switchMode('forgot-password')}
                            className="text-xs text-primary hover:text-primary/80 transition-colors"
                          >
                            {dict.login.forgotPassword}
                          </button>
                        </div>
                        <div className="relative">
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder={dict.login.passwordPlaceholder}
                            className="h-12 pr-10"
                            autoComplete="current-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>

                      {error && (
                        <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-xl p-3">
                          {error}
                        </div>
                      )}

                      <Button
                        type="submit"
                        disabled={loading || !email || !password}
                        className="w-full h-12 text-sm font-semibold"
                      >
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            {dict.login.signingIn}
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            {dict.login.signIn}
                            <ArrowRight className="h-4 w-4" />
                          </span>
                        )}
                      </Button>

                      <div className="text-center">
                        <button
                          type="button"
                          onClick={() => switchMode('magic-link')}
                          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {dict.login.orMagicLink}
                        </button>
                      </div>
                    </form>
                  )}

                  {/* Magic link form */}
                  {authMode === 'magic-link' && (
                    <form onSubmit={handleMagicLink} className="space-y-3.5">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">
                          {dict.login.emailLabel}
                        </label>
                        <Input
                          type="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="your@email.com"
                          className="h-12"
                          autoComplete="email"
                        />
                      </div>

                      {error && (
                        <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-xl p-3">
                          {error}
                        </div>
                      )}

                      <Button
                        type="submit"
                        disabled={loading || !email}
                        className="w-full h-12 text-sm font-semibold"
                      >
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            {dict.login.sending}
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            {dict.login.sendLink}
                            <ArrowRight className="h-4 w-4" />
                          </span>
                        )}
                      </Button>

                      <div className="text-center">
                        <button
                          type="button"
                          onClick={() => switchMode('password')}
                          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {dict.login.orPassword}
                        </button>
                      </div>
                    </form>
                  )}

                  {/* Forgot password form */}
                  {authMode === 'forgot-password' && (
                    <form onSubmit={handleForgotPassword} className="space-y-3.5">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">
                          {dict.login.emailLabel}
                        </label>
                        <Input
                          type="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="your@email.com"
                          className="h-12"
                          autoComplete="email"
                        />
                      </div>

                      {error && (
                        <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-xl p-3">
                          {error}
                        </div>
                      )}

                      <Button
                        type="submit"
                        disabled={loading || !email}
                        className="w-full h-12 text-sm font-semibold"
                      >
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            {dict.login.sendingReset}
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            {dict.login.sendResetLink}
                            <ArrowRight className="h-4 w-4" />
                          </span>
                        )}
                      </Button>

                      <div className="text-center">
                        <button
                          type="button"
                          onClick={() => switchMode('password')}
                          className="text-sm text-primary hover:text-primary/80 font-medium transition-colors"
                        >
                          {dict.login.backToSignIn}
                        </button>
                      </div>
                    </form>
                  )}
                </div>

                <p className="text-center text-xs text-muted-foreground/50 mt-5">
                  {dict.login.footer}
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
